import React, { useCallback, useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import TableLayout from "../../layouts/TableLayout/NewTableLayout";
import { useAuth } from "../../hooks/useAuth";
import {
  useGetEquipmentQuery,
  useUpdateEquipmentMutation,
} from "../../redux/services/equipment";
import { usePrefetch } from "../../redux/api";
import { useDebounceSearch } from "../../hooks";
import { removeEmptyValues } from "../../utils";
import { usePermissions } from "../../hooks/usePermissions";
import { PERMISSIONS } from "../../constants/access-control";
import { COLUMN_DEFINITIONS } from "./table.config";
import { DeleteEquipmentModal } from "./DeleteModal";
import EquipmentCreateModal from "./CreateModal";
import { EquipmentTagsFilterList } from "../../components";

const FLEET_CATEGORY_TABS = [
  { id: "all", label: "All" },
  { id: "truck", label: "Trucks" },
  { id: "trailer", label: "Trailers" },
];

const Equipment = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryFilter = searchParams.get("category") || "all";
  const tagsFilterParam = searchParams.get("tags");
  const { hasAnyPermission } = usePermissions();
  const canManageEquipment = hasAnyPermission([
    PERMISSIONS.EQUIPMENT_CREATE,
    PERMISSIONS.EQUIPMENT_EDIT,
    PERMISSIONS.EQUIPMENT_DELETE,
  ]);

  const [deselectItems, setDeselectItems] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [deleteVisible, setDeleteVisible] = useState(false);
  const sendSelectedItems = useCallback((value) => setSelectedItems(value), []);

  const { search, handleSearch } = useDebounceSearch();

  const [filters, setFilters] = useState([]);
  const [{ pageIndex, pageSize }, setPagination] = useState({
    pageIndex: 0,
    pageSize: 20,
  });

  const searchFilters = useMemo(() => {
    return filters.reduce((a, v) => {
      if (v.id === "date") {
        return {
          ...a,
          startDate: v.value?.start.toISOString(),
          endDate: v.value?.end.toISOString(),
        };
      }
      return { ...a, [v.id]: v.value };
    }, {});
  }, [filters]);

  const {
    data: equipmentData,
    isLoading,
    isFetching,
  } = useGetEquipmentQuery({
    page: pageIndex + 1,
    limit: pageSize,
    search: search.useSearch,
    searchTerm: search.term,
    ...(categoryFilter !== "all"
      ? { equipmentCategory: categoryFilter }
      : {}),
    ...removeEmptyValues(searchFilters),
  });

  const data = useMemo(() => {
    if (!equipmentData?.equipment) return [];
    return equipmentData.equipment.filter(
      (item) => item !== null && item !== undefined
    );
  }, [equipmentData]);

  const paginationData = useMemo(
    () => ({
      hasNext: equipmentData?.hasNext || false,
      hasPrev: equipmentData?.hasPrev || false,
      totalPages: equipmentData?.totalPages || 0,
      page: equipmentData?.page || 1,
      count: equipmentData?.count || 0,
    }),
    [equipmentData]
  );

  const [updateEquipment] = useUpdateEquipmentMutation();

  const prefetchPage = usePrefetch("getEquipment");

  const prefetchNext = useCallback(() => {
    prefetchPage({
      page: data.nextPage,
      limit: pageSize,
      search: search.useSearch,
      searchTerm: search.term,
      ...removeEmptyValues(searchFilters),
    });
  }, [
    prefetchPage,
    data?.nextPage,
    pageSize,
    search.useSearch,
    search.searchTerm,
  ]);

  useEffect(() => {
    if (data?.hasNext && data.nextPage <= data.totalPages) {
      prefetchNext();
    }
  }, [data, pageIndex, prefetchNext]);

  const [createModalOpen, setCreateModalOpen] = useState(false);

  const handleEdit = useCallback(
    (itemId) => {
      navigate(`/equipment/${itemId}`);
    },
    [navigate]
  );

  const handleUpdate = useCallback(
    async (e, setLoading) => {
      try {
        const res = await updateEquipment({
          id: e.row.original._id,
          ...e.data,
        }).unwrap();

        toast(res.message, {
          type: res.status,
          toastId: `update-equipment-${res.status}`,
        });
      } catch {
        toast.error("Failed to update equipment");
      } finally {
        setTimeout(() => {
          setLoading(false);
        }, 500);
      }
    },
    [updateEquipment]
  );

  const handleDeleteClick = useCallback(() => {
    setDeleteVisible(true);
  }, []);

  const cannotCreateOrEdit = !canManageEquipment;

  const globalFilter = useCallback(
    (value) => {
      setPagination({ pageIndex: 0, pageSize });
      handleSearch(value);
    },
    [pageSize, handleSearch]
  );

  const filterTypes = useMemo(
    () => [
      {
        id: "date",
        type: "date",
        label: "Date",
        defaultText: "All Time",
        defaultValue: {
          id: "allTime",
          value: null,
        },
      },
      {
        id: "tags",
        type: "multiple",
        label: "Tags",
        defaultText: "All Tags",
        content: EquipmentTagsFilterList,
      },
    ],
    []
  );

  const initialTagFilters = useMemo(() => {
    if (!tagsFilterParam) return undefined;
    const tagFilterDef = filterTypes.find((filter) => filter.id === "tags");
    if (!tagFilterDef) return undefined;
    return [
      {
        ...tagFilterDef,
        type: "multiple",
        value: tagsFilterParam,
        displayText: "Selected Tags",
      },
    ];
  }, [tagsFilterParam, filterTypes]);

  useEffect(() => {
    if (tagsFilterParam) {
      setFilters([{ id: "tags", value: tagsFilterParam }]);
    }
  }, [tagsFilterParam]);

  const handleCategoryTab = useCallback(
    (tabId) => {
      const next = new URLSearchParams(searchParams);
      if (tabId === "all") {
        next.delete("category");
      } else {
        next.set("category", tabId);
      }
      setSearchParams(next);
      setPagination({ pageIndex: 0, pageSize });
    },
    [pageSize, searchParams, setSearchParams]
  );

  return (
    <div className="flex h-full min-h-0 w-full flex-col">
      <div className="mb-4 flex shrink-0 flex-wrap gap-2 px-1">
        {FLEET_CATEGORY_TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => handleCategoryTab(tab.id)}
            className={`rounded-md px-3 py-1.5 text-sm font-medium ${
              categoryFilter === tab.id
                ? "bg-primary-600 text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>
      <div className="min-h-0 flex-1 h-full w-full">
      <TableLayout
        id="equipment"
        title={t("equipment.main")}
        buttonSize="xs"
        loading={isLoading}
        columns={COLUMN_DEFINITIONS} // Define COLUMN_DEFINITIONS for equipment
        data={data}
        pagination={paginationData}
        pageIndex={pageIndex}
        pageSize={pageSize}
        onPaginationChange={setPagination}
        pageSizeOptions={[10, 20, 30, 40, 50]} // Define PAGE_SIZE_OPTIONS for equipment
        handleEdit={handleEdit}
        onUpdate={handleUpdate}
        mainAction={{ onClick: () => setCreateModalOpen(true) }}
        hideActions={cannotCreateOrEdit}
        hideMainAction={cannotCreateOrEdit}
        globalSearchFilters={["date", "equipmentNumber"]} // Adjust as needed for equipment
        fullTextSearch={globalFilter}
        sendSelectedItems={sendSelectedItems}
        handleDeleteClick={handleDeleteClick}
        clearSelectedItems={deselectItems}
        filters={filterTypes}
        onFiltersChange={setFilters}
        initialFilters={initialTagFilters}
        hideCheckbox={cannotCreateOrEdit}
      />
      </div>
      <EquipmentCreateModal
        modalOpen={createModalOpen}
        setModalOpen={setCreateModalOpen}
      />
      <DeleteEquipmentModal
        deselectItems={() => {
          setDeselectItems(true);
          setTimeout(() => setDeselectItems(false), 500);
        }}
        visible={deleteVisible}
        setVisible={setDeleteVisible}
        equipment={selectedItems}
      />
    </div>
  );
};

export default React.memo(Equipment);
